import dayjs from 'dayjs'
import draftToHtml from 'draftjs-to-html'

import { GRAPH_COLORS } from 'consts'
import { PinType, WorkspaceJobType } from 'generated/graphql'
import type { TApolloCache } from 'lib/apollo-client'
import { GLOBAL_TAXONOMY_TERRITORY_TYPES } from 'modules/global-topic/common/global-topic.consts'
import { getMonthNumberFromString, getTolgeeDateFromString } from 'utils/dateUtils'
import { jsonUtils } from 'utils/jsonUtils'
import TOLGEE_GLOBALS from 'utils/tolgeeGlobals'

import { PIN_TYPE_BY_SELECTED_TYPE, PIN_TYPE_TO_FIELD_MAP } from './dashboard.consts'
import type {
  TPinChanges,
  TPinCacheOperation,
  IPinItem,
  IWorkspaceJobPinsCache,
  IJobPinInput,
  IJobPinSelectedItem,
} from './dashboard.types'

type DraftTextContent = {
  blocks: Record<string, any>[]
  entityMap: Record<string, any>
}

function isValidHttpUrl(string) {
  let url

  try {
    url = new URL(string)
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_) {
    return false
  }

  return url.protocol === 'http:' || url.protocol === 'https:'
}

function getNewUrl(path: string) {
  const baseURL = new URL(location.href)
  baseURL.pathname = path
  return baseURL.toString()
}

export const getTextContent = (textContent?: string | DraftTextContent): string => {
  const isString = typeof textContent === 'string'
  if (isString) {
    return textContent
  }
  if (!textContent) {
    return ''
  }

  const editedBlocks = textContent?.blocks?.map(item => {
    return {
      ...item,
      entityRanges: [],
    }
  })

  const newTextValue = {
    ...textContent,
    blocks: editedBlocks,
  }
  const draftAsHTML: string = draftToHtml(newTextValue)
  const hasLink = textContent?.entityMap?.[0]?.type === 'LINK'
  if (hasLink) {
    const hasLinkInHTML = draftAsHTML.includes('<a')
    if (!hasLinkInHTML) {
      const textLink = textContent?.entityMap?.[0]?.data?.url
      const isValid = isValidHttpUrl(textLink)
      const finalLink = isValid ? textLink : getNewUrl(textLink)
      const newText = `<a href="${finalLink}">${draftAsHTML}</a>`
      return newText
    }
  }
  return draftAsHTML
}

/**
 * Date must be in the format 'MMM/YYYY'
 */
const convertMonthStringToNumber = (date: string) => {
  const splitDate = date.split('/')
  const month = splitDate[0].toLocaleLowerCase()
  const year = splitDate[1]
  const monthNumber = getMonthNumberFromString(month)
  return `${year}-${monthNumber}-01`
}

export function formatLockedRelevanceChart(lockedData, dateRange, interval) {
  const shouldFormatDate = dateRange === 1 || ['month', 'quarter'].includes(interval)
  const metricData = lockedData.metricData ?? []
  const newMetricData = metricData.map(item => {
    const newData = item.data.map(data => {
      const date = !shouldFormatDate ? data.x : dayjs(convertMonthStringToNumber(data.x)).format('MMM/YYYY')
      return {
        y: data.y,
        x: date,
      }
    })
    return {
      ...item,
      data: newData,
    }
  })
  return {
    territories: lockedData.territories,
    metricData: newMetricData,
  }
}

export function getAudienceText(data, t) {
  const baseTitle = ['Audiência estimada para ', 'Estimated audience for ', 'Audiencia estimada para ']
  const title = data.title
  const subtitle = data.subtitle

  const territoryName = baseTitle.reduce((prev, curr) => {
    return prev.replace(curr, '')
  }, title)

  const isCustomDate = subtitle.includes('-')

  /**
   * Date must be in the format 'MMM DD, YYY'
   */
  const getCustomDateValues = date => {
    const splitDate = date.split(' ').filter(item => item)
    const month = getMonthNumberFromString(splitDate[0])
    return `${splitDate[2]}-${month}-${splitDate[1]}`
  }

  /**
   * Date must be in the format 'MMM DD, YYY - MMM DD, YYYY'
   */
  const getCustomDate = () => {
    const dates = subtitle.split('-')
    const initialDate = getCustomDateValues(dates[0])
    const finalDate = getCustomDateValues(dates[1])
    return `${dayjs(initialDate).format('MMM DD, YYYY')} - ${dayjs(finalDate).format('MMM DD, YYYY')}`
  }

  const finalTitle = t({
    key: `AudienceComponent-estimated-audience-territoy`,
    parameters: { title: territoryName },
    defaultValue: `Estimated audience for {title}`,
  })

  return {
    title: finalTitle,
    subtitle: isCustomDate ? getCustomDate() : getTolgeeDateFromString(subtitle, t),
  }
}

export function getShouldDisplayBanner(type) {
  return [
    WorkspaceJobType.AutoBriefingSocial,
    WorkspaceJobType.Briefing,
    WorkspaceJobType.AutoTerritoryAnalysis,
    WorkspaceJobType.AutoDemandSpaces,
  ].includes(type)
}

export function getDashboardBannerInformation({ name, workspaceBrand, dashboard, t }) {
  if (dashboard?.type === 'auto_territory_analysis') {
    return {
      color: 'var(--color_neutral_500)',
      name: name,
      subtitle: t(TOLGEE_GLOBALS.territoryAnalysis),
    }
  }
  if (dashboard?.type === WorkspaceJobType.AutoDemandSpaces) {
    return {
      name,
      color: 'var(--color_neutral_500)',
      subtitle: t(TOLGEE_GLOBALS.demandSpaces),
    }
  }
  const thumbnail = workspaceBrand?.thumbnail
  const color = workspaceBrand?.customSettings
    ? jsonUtils.parse(workspaceBrand?.customSettings)?.color
    : GRAPH_COLORS[0]

  return {
    name,
    color,
    thumbnail,
  }
}

export function getTerritoryCompareDataUniverseSubtitleData(territories) {
  const initialValue = {
    views: 0,
    engagement: 0,
    videos: 0,
  }
  if (!territories.length) {
    return initialValue
  }
  const subtitleData = territories.reduce((prev, curr) => {
    const views = curr?.territoryPerformance?.metrics?.totalViews
    const engagement = curr?.territoryPerformance?.metrics?.totalEngagement
    const posts = curr?.territoryPerformance?.totalPosts
    return {
      views: prev.views + views,
      engagement: prev.engagement + engagement,
      videos: prev.videos + posts,
    }
  }, initialValue)
  return subtitleData
}

/**
 * Print Layout Restructure Utility
 * Group ALL items into rows and put one row per A3 page
 */

// Grid cell height from GridstackStaticContent config
const GRID_CELL_HEIGHT = 48 // pixels per grid unit (gs-h=1)

/**
 * Check if two items overlap vertically
 */
function itemsOverlapVertically(item1Y, item1H, item2Y, item2H) {
  const item1Start = item1Y
  const item1End = item1Y + item1H
  const item2Start = item2Y
  const item2End = item2Y + item2H

  return !(item1End <= item2Start || item2End <= item1Start)
}

/**
 * Restructure layout - one row per A3 page
 */
export function restructureForPrint() {
  const A3_PAGE_WIDTH_AT_110_DPI = 1286 // Width: 297mm = 1286px at 110 DPI
  const A3_USABLE_HEIGHT_AT_110_DPI = 1776 // Usable height with margins, must be a multiple of GRID_CELL_HEIGHT
  const pageWidth = A3_PAGE_WIDTH_AT_110_DPI
  const pageHeight = A3_USABLE_HEIGHT_AT_110_DPI
  const pageSize = { width: pageWidth, height: pageHeight }

  // Find main grid
  const gridContainer = document.querySelector('.grid-stack:not(.grid-stack-nested)')
  if (!gridContainer) {
    return
  }

  // Get sections
  const sections = gridContainer.querySelectorAll('.grid-stack-nested')

  // Process each section and collect ALL rows
  const allRows = []
  let rowOffset = 0

  sections.forEach(section => {
    const items = Array.from(section.querySelectorAll(':scope > .grid-stack-item'))

    if (items.length === 0) return

    // Sort items by Y position first to ensure correct grouping
    items.sort((a, b) => {
      const aY = parseInt(a.getAttribute('gs-y')) || 0
      const bY = parseInt(b.getAttribute('gs-y')) || 0
      return aY - bY
    })

    // Group this section's items into rows
    const sectionRows = []

    items.forEach(item => {
      const gsY = parseInt(item.getAttribute('gs-y')) || 0
      const gsH = parseInt(item.getAttribute('gs-h')) || 1
      const gsX = parseInt(item.getAttribute('gs-x')) || 0
      const gsW = parseInt(item.getAttribute('gs-w')) || null

      // Find if this item overlaps with any existing row in THIS section
      let foundRow = null
      for (const row of sectionRows) {
        const overlaps = row.items.some(rowItem => {
          const rowY = parseInt(rowItem.element.getAttribute('gs-y')) || 0
          const rowH = parseInt(rowItem.element.getAttribute('gs-h')) || 1
          const doesOverlap = itemsOverlapVertically(gsY, gsH, rowY, rowH)
          return doesOverlap
        })

        if (overlaps) {
          foundRow = row
          break
        }
      }

      if (foundRow) {
        foundRow.items.push({ element: item, x: gsX, y: gsY, w: gsW, h: gsH })
      } else {
        sectionRows.push({
          minY: gsY,
          globalRowNumber: rowOffset + sectionRows.length,
          items: [{ element: item, x: gsX, y: gsY, w: gsW, h: gsH }],
        })
      }
    })

    // Sort this section's rows by Y position
    sectionRows.sort((a, b) => a.minY - b.minY)

    // Update global row numbers after sorting
    sectionRows.forEach((row, idx) => {
      row.globalRowNumber = rowOffset + idx
    })

    // Add to all rows
    allRows.push(...sectionRows)

    // Update offset for next section
    rowOffset += sectionRows.length
  })

  if (allRows.length === 0) {
    return
  }

  // Store reference to first section to append pages
  const firstSection = sections[0]
  if (!firstSection) {
    return
  }

  // Get the background color from the section's parent (grid-stack-item-content or dashboardSection)
  const sectionParent = firstSection.closest('.dashboardSection')
  const sectionBgColor = sectionParent ? window.getComputedStyle(sectionParent).backgroundColor : 'white'

  // Calculate header height for first page
  // On the first page, we need to account for title-row and DashboardBanner-wrapper
  const titleRow = document.querySelector('.title-row')
  const dashboardBanner = document.querySelector('.DashboardBanner-wrapper')

  let firstPageHeaderHeight = 0
  if (titleRow) {
    const titleRect = titleRow.getBoundingClientRect()
    const titleStyle = window.getComputedStyle(titleRow)
    const titleHeight = titleRect.height + parseFloat(titleStyle.marginTop) + parseFloat(titleStyle.marginBottom)
    firstPageHeaderHeight += titleHeight
  }
  if (dashboardBanner) {
    const bannerRect = dashboardBanner.getBoundingClientRect()
    const bannerStyle = window.getComputedStyle(dashboardBanner)
    const bannerHeight = bannerRect.height + parseFloat(bannerStyle.marginTop) + parseFloat(bannerStyle.marginBottom)
    firstPageHeaderHeight += bannerHeight
  }

  // Create pages and pack rows
  let currentPage = null
  let currentPageHeight = 0
  const pageMargin = 40
  let currentPageContainer = null
  let currentPageNumber = 0 // Track which page we're currently on (0 = first page, 1 = second, etc.)
  let previousRowEndY = 0 // Track the end Y position of the previous row

  allRows.forEach(row => {
    // Calculate row height based on tallest item
    row.items.sort((a, b) => a.x - b.x)

    let maxHeight = 0
    row.items.forEach(({ element }) => {
      const rect = element.getBoundingClientRect()
      if (rect.height > maxHeight) {
        maxHeight = rect.height
      }
    })

    const rowHeight = maxHeight || 200 // fallback to 200px

    // Calculate available height based on which page we're currently filling
    // First page (0) needs reduced height to account for headers above it
    const availableHeight =
      currentPageNumber === 0
        ? pageSize.height - pageMargin * 2 - firstPageHeaderHeight
        : pageSize.height - pageMargin * 2

    // Check if we need a new page
    if (!currentPage || currentPageHeight + rowHeight + 16 > availableHeight) {
      // If we already have a page, we're moving to the next one
      const isCreatingFirstPage = !currentPage
      if (currentPage) {
        currentPageNumber++ // Increment when leaving a page to go to next one
      }

      // Create new A3 page
      currentPage = document.createElement('div')
      currentPage.className = 'debug-a3-page'
      currentPage.style.width = `${pageSize.width}px`

      // First page should be shorter to account for headers above it
      // Subsequent pages use full height
      const actualPageHeight = isCreatingFirstPage ? pageSize.height - firstPageHeaderHeight : pageSize.height

      currentPage.style.minHeight = `${actualPageHeight}px`
      currentPage.style.maxHeight = `${actualPageHeight}px`
      currentPage.style.height = `${actualPageHeight}px`
      currentPage.style.marginBottom = '20px'
      currentPage.style.padding = '0px'
      currentPage.style.boxSizing = 'border-box'
      currentPage.style.backgroundColor = sectionBgColor
      currentPage.style.position = 'relative'
      currentPage.style.overflow = 'hidden' // Prevent content from overflowing

      // Force page break after first page
      if (isCreatingFirstPage) {
        currentPage.style.pageBreakAfter = 'always'
        currentPage.style.breakAfter = 'page'
      }

      // Create container for rows inside page
      currentPageContainer = document.createElement('div')
      currentPageContainer.style.position = 'relative'
      currentPageContainer.style.width = '100%'
      currentPage.appendChild(currentPageContainer)

      firstSection.parentNode.appendChild(currentPage)
      currentPageHeight = 0
    }

    // Calculate gap between previous row and current row
    const currentRowStartY = row.minY
    const gapBetweenRows =
      currentRowStartY > previousRowEndY ? (currentRowStartY - previousRowEndY) * GRID_CELL_HEIGHT : 0

    // Create actual row container
    const actualRowContainer = document.createElement('div')
    actualRowContainer.className = 'print-row'
    actualRowContainer.style.position = 'relative'
    actualRowContainer.style.width = '100%'
    actualRowContainer.style.marginTop = `${gapBetweenRows}px`
    actualRowContainer.style.marginBottom = '16px'
    // Don't set minHeight - let content naturally define height

    // Add items to row with relative positioning based on row's minY
    const rowMinY = row.minY
    row.items.forEach(({ element, x, y, w, h }) => {
      element.style.position = 'absolute'
      element.style.left = `${(x / 12) * 100}%`
      // Calculate relative top position within this row
      const relativeY = (y - rowMinY) * GRID_CELL_HEIGHT // Convert grid units to pixels
      element.style.top = `${relativeY}px`
      if (w) {
        element.style.width = `${(w / 12) * 100}%`
      }
      element.style.height = `${h * GRID_CELL_HEIGHT}px`

      // Check if element contains an ImageComponentWrapper and apply proper image styles
      const imageWrapper = element.querySelector('.ImageComponentWrapper')
      if (imageWrapper) {
        const img = imageWrapper.querySelector('img')
        if (img) {
          img.style.maxWidth = '100%'
          img.style.height = `${h * GRID_CELL_HEIGHT}px`
          img.style.objectFit = 'contain'
        }
      }

      actualRowContainer.appendChild(element)
    })

    currentPageContainer.appendChild(actualRowContainer)

    // Calculate row height based on the bottom of the lowest item
    let maxBottom = 0
    row.items.forEach(({ element, y }) => {
      const relativeY = (y - rowMinY) * GRID_CELL_HEIGHT
      const childHeight = element.offsetHeight
      const bottom = relativeY + childHeight
      if (bottom > maxBottom) {
        maxBottom = bottom
      }
    })

    // Use calculated height or fallback
    const finalHeight = maxBottom > 0 ? maxBottom : rowHeight
    actualRowContainer.style.height = `${finalHeight}px`

    // Update previousRowEndY for next iteration
    // Calculate the maximum Y + H from all items in this row
    let maxItemEndY = 0
    row.items.forEach(({ y, h }) => {
      const itemEndY = y + h
      if (itemEndY > maxItemEndY) {
        maxItemEndY = itemEndY
      }
    })
    previousRowEndY = maxItemEndY

    currentPageHeight += gapBetweenRows + finalHeight + 16
  })

  // Hide all original sections
  sections.forEach((section: HTMLElement) => {
    section.style.display = 'none'
  })

  return () => {
    //TODO: Implement cleanup to restore original layout if needed
  }
}

export const getPinChangesFromItems = (itemsList: IPinItem[]): TPinChanges => {
  return itemsList.reduce<TPinChanges>(
    (acc, item) => {
      const field = PIN_TYPE_TO_FIELD_MAP[item?.type] || 'territoriesAmount'
      acc[field] = (acc[field] || 0) + 1
      acc.totalPins += 1
      return acc
    },
    { creatorAmount: 0, postAmount: 0, territoriesAmount: 0, totalPins: 0 }
  )
}

export const getJobPinInput = ({ id, type, provider }: IJobPinSelectedItem, folders: string[]): IJobPinInput => {
  if (type && GLOBAL_TAXONOMY_TERRITORY_TYPES.includes(type)) {
    return {
      provider: provider ?? null,
      type: PinType.TaxonomyTerritories,
      entityId: `${type}.${id}`,
      folders,
    }
  }

  return {
    provider: provider ?? null,
    type: (type && PIN_TYPE_BY_SELECTED_TYPE[type]) ?? type,
    entityId: String(id),
    folders,
  }
}

export const updateWorkspaceJobPinsCache = (
  cache: TApolloCache,
  jobId: string,
  pinChanges: TPinChanges,
  operation: TPinCacheOperation = 'add'
): void => {
  if (!jobId) return

  const multiplier = operation === 'add' ? 1 : -1

  cache.modify({
    id: `WorkspaceJob:${jobId}`,
    fields: {
      pins: existingPins => {
        const existingPinsTyped = existingPins as IWorkspaceJobPinsCache | undefined | null
        const creatorAmount = Math.max(
          0,
          Number(existingPinsTyped?.creatorAmount ?? 0) + pinChanges.creatorAmount * multiplier
        )
        const postAmount = Math.max(0, Number(existingPinsTyped?.postAmount ?? 0) + pinChanges.postAmount * multiplier)
        const territoriesAmount = Math.max(
          0,
          Number(existingPinsTyped?.territoriesAmount ?? 0) + pinChanges.territoriesAmount * multiplier
        )
        const totalPins = Math.max(0, Number(existingPinsTyped?.totalPins ?? 0) + pinChanges.totalPins * multiplier)

        return {
          ...(existingPinsTyped || {}),
          creatorAmount,
          postAmount,
          subtopicAmount: existingPinsTyped?.subtopicAmount ?? 0,
          topicAmount: existingPinsTyped?.topicAmount ?? 0,
          savedSearchesAmount: existingPinsTyped?.savedSearchesAmount ?? 0,
          territoriesAmount,
          totalPins,
        }
      },
      updatedAt: () => new Date().toISOString(),
    },
  })
}

export const renderQuadrantHighlight = function (
  this: Highcharts.Chart & {
    quadrantHighlight?: Highcharts.SVGElement
  }
) {
  const xAxis = this.xAxis[0]
  const yAxis = this.yAxis[0]

  if (!xAxis || !yAxis) return

  this.quadrantHighlight?.destroy()

  const xMin = xAxis.min ?? 0
  const xMax = xAxis.max ?? 0
  const yMin = yAxis.min ?? 0
  const yMax = yAxis.max ?? 0

  const rectX = xAxis.toPixels((xMin + xMax) / 2, false)
  const rectY = yAxis.toPixels(yMax, false)
  const rectWidth = xAxis.toPixels(xMax, false) - rectX
  const rectHeight = yAxis.toPixels((yMin + yMax) / 2, false) - rectY

  this.quadrantHighlight = this.renderer
    .rect(rectX, rectY, rectWidth, rectHeight)
    .attr({
      fill: 'rgba(148, 156, 171, 0.12)',
      zIndex: 0,
    })
    .add()
}

export const lowHighLabelFormatter = function (this: Highcharts.AxisLabelsFormatterContextObject) {
  if (this.isFirst) return 'Low'
  if (this.isLast) return 'High'
  return ''
}
